/**
 * <swatch-carousel-latori> pages through the color swatch grid in the
 * variant picker using Swiper's Grid module (fixed rows x columns per page),
 * with numbered page pills instead of dots or "X of Y" text.
 *
 * Only becomes active when there are more swatches than fit in a single
 * page on EVERY breakpoint tier; Swiper's own lock/unlock (per current
 * breakpoint) handles the case where paging is only needed on some of them.
 *
 * Data attributes:
 * - data-breakpoints  Required. JSON object mapping a min-width (px, "0" for
 *                      the base/smallest tier) to { rows, cols } for that
 *                      tier, e.g.:
 *                      '{"0":{"rows":4,"cols":4},"768":{"rows":2,"cols":8}}'
 *
 * Expected markup:
 * <swatch-carousel-latori data-breakpoints='{"0":{"rows":4,"cols":4}}'>
 *   <div class="swatch-carousel-latori__skeleton">...</div>
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">...</div>
 *     </div>
 *   </div>
 *   <div class="slider-nav">
 *     <button name="prev">...</button>
 *     <div class="swiper-pagination"></div>
 *     <button name="next">...</button>
 *   </div>
 * </swatch-carousel-latori>
 *
 * The skeleton is rendered server-side (not built by this script) and
 * shown/hidden purely by CSS keyed off [loaded]/[inactive] - see
 * variant-picker.liquid - specifically so it's visible from first paint
 * with no dependency on this (deferred) script having run yet. init()
 * still removes the now-unneeded skeleton node once Swiper is up.
 *
 * The whole file is wrapped in the same "already registered" guard as
 * variant-picker.js/custom-select.js: it's loaded from both the PDP variant
 * picker (variant-picker.liquid) and the model configurator
 * (configurator-button.liquid), and when both render on one page the second
 * copy of this script must be a no-op - an unguarded top-level class
 * declaration would already throw a redeclaration SyntaxError at parse time.
 */
if (!customElements.get("swatch-carousel-latori")) {
  class SwatchCarouselLatori extends HTMLElement {
    connectedCallback() {
      // Selecting a variant re-inserts this same (already-initialized) node
      // elsewhere in the DOM rather than replacing it - see the
      // "before-replace" listener below. That's a second connect, not a
      // fresh mount, so skip straight past re-running init().
      if (this.hasAttribute("loaded")) return;

      this.swiperEl = this.querySelector(".swiper");
      this.slides = this.swiperEl
        ? [...this.swiperEl.querySelectorAll(".swiper-slide")]
        : [];
      this.tiers = this.parseTiers();

      const capacities = Object.values(this.tiers).map((t) => t.rows * t.cols);
      if (this.slides.length <= Math.min(...capacities)) {
        // Reselecting a variant moves (not recreates) this same node - see
        // the "before-replace" listener below - which re-triggers
        // connectedCallback() same as any other DOM move. The [inactive]
        // attribute survives that move, so it doubles as the same kind of
        // "already set up, skip re-running" guard [loaded] is for the
        // active path below - without it, syncStaticSwatchSize() would
        // stack a new ResizeObserver on the same slide on every reselect.
        if (!this.hasAttribute("inactive")) {
          this.setAttribute("inactive", "");
          this.syncStaticSwatchSize();
        }
        return;
      }

      window.initLazyScript(this, this.init.bind(this));
    }

    parseTiers() {
      try {
        const tiers = JSON.parse(this.dataset.breakpoints || "");
        if (tiers && Object.keys(tiers).length) return tiers;
      } catch (e) {
        // fall through to default below
      }
      return { 0: { rows: 2, cols: 8 } };
    }

    init() {
      if (this.hasAttribute("loaded") || typeof Swiper === "undefined") return;

      const prevBtn = this.querySelector('button[name="prev"]');
      const nextBtn = this.querySelector('button[name="next"]');
      const paginationEl = this.querySelector(".swiper-pagination");

      const widths = Object.keys(this.tiers)
        .map(Number)
        .sort((a, b) => a - b);
      const base = this.tiers[widths[0]];
      const breakpoints = {};
      widths.slice(1).forEach((width) => {
        const tier = this.tiers[width];
        breakpoints[width] = {
          slidesPerView: tier.cols,
          slidesPerGroup: tier.cols,
          grid: { rows: tier.rows, fill: "row" },
        };
      });

      this.swiper = new Swiper(this.swiperEl, {
        slidesPerView: base.cols,
        slidesPerGroup: base.cols,
        spaceBetween: 12,
        grid: { rows: base.rows, fill: "row" },
        watchOverflow: true,
        a11y: true,
        breakpoints,
        navigation:
          prevBtn && nextBtn ? { prevEl: prevBtn, nextEl: nextBtn } : false,
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
              renderBullet: (index, className) =>
                `<button type="button" class="${className}">${
                  index + 1
                }</button>`,
            }
          : false,
        on: {
          lock: () => this.setAttribute("inactive", ""),
          unlock: () => this.removeAttribute("inactive"),
          resize: () => this.syncSwatchSize(),
          // A tier change re-chunks the pages, which can leave the checked
          // swatch stranded on a no-longer-visible page - snap back to it.
          breakpoint: () => this.revealCheckedSlide(0),
        },
      });

      if (this.swiper.isLocked) this.setAttribute("inactive", "");

      this.syncSwatchSize();
      this.revealCheckedSlide(0);
      this.querySelector(".swatch-carousel-latori__skeleton")?.remove();
      this.setAttribute("loaded", "");
    }

    /**
     * Pages to whichever page holds the currently checked swatch (the
     * preselected color, or a ?variant= deep link's), so the current
     * selection is never hidden behind page 2+ - the swiper equivalent of
     * the old configurator pagination's "start on the checked page".
     *
     * Index math: with fill:"row" each pagination page holds rows*cols
     * CONSECUTIVE DOM slides (row-major within the page), but Grid's
     * slideTo() counts virtual COLUMNS of `rows` slides each - passing a
     * DOM slide index scrolls to the wrong page (it merely clamps into
     * place on the last page, which is what made the naive version look
     * right on 2-page carousels). A page therefore starts at column
     * page*cols, and that is the unit slideTo() needs.
     */
    revealCheckedSlide(speed) {
      if (!this.swiper || this.swiper.destroyed) return;
      const checkedIndex = this.slides.findIndex((slide) =>
        slide.querySelector("input:checked"),
      );
      if (checkedIndex < 0) return;
      const params = this.swiper.params;
      const rows = params.grid?.rows || 1;
      const cols = params.slidesPerView;
      const page = Math.floor(checkedIndex / (cols * rows));
      this.swiper.slideTo(page * cols, speed);
    }

    /**
     * Keeps each swatch exactly as wide as its column so the horizontal gap
     * between swatches (spaceBetween) matches the vertical row gap, while
     * still letting the grid stretch to fill the full available width.
     * Calls swiper.update() afterwards so Swiper re-measures the (now taller
     * or shorter) slide content and recalculates row height/position to
     * match — relying on Swiper's own row math instead of overriding it via
     * CSS keeps the vertical gap correctly in sync with spaceBetween.
     */
    syncSwatchSize() {
      const slide = this.swiperEl.querySelector(".swiper-slide");
      if (!slide) return;
      // Swiper has already run its own update() by the time it emits
      // "resize", so a second one is only needed when the swatch size
      // actually changed. update() forces a style recalc per slide - on a
      // 100+ swatch carousel that's hundreds of ms - and the height change
      // it causes re-triggers Swiper's ResizeObserver, so an unconditional
      // update here ran it again on every one of those rounds.
      const size = `${slide.offsetWidth}px`;
      if (
        this.swiperEl.style.getPropertyValue("--swatch-picker-image-size") ===
        size
      ) {
        return;
      }
      this.swiperEl.style.setProperty("--swatch-picker-image-size", size);
      this.swiper.update();
    }

    /**
     * A carousel with too few swatches to ever need paging skips Swiper
     * entirely (see connectedCallback() above), so there's no swiper
     * "resize" event to drive syncSwatchSize() and keep swatches sized to
     * their column, like an active carousel's swatches are. Without this,
     * such swatches would just sit at settings.swatch_picker_image_size's
     * static default - usually smaller than an active carousel's own
     * per-column width - making the two look inconsistently sized. The
     * plain CSS grid this falls back to (variant-picker.liquid) still gives
     * each swatch a same-sized column to measure, so a ResizeObserver here
     * (standing in for Swiper's own resize event) keeps
     * --swatch-picker-image-size in sync with it, matching an active
     * carousel's sizing exactly whenever the breakpoint's column count
     * changes the column's width.
     */
    syncStaticSwatchSize() {
      const slide = this.swiperEl?.querySelector(".swiper-slide");
      if (!slide) return;
      const sync = () =>
        this.swiperEl.style.setProperty(
          "--swatch-picker-image-size",
          `${slide.offsetWidth}px`,
        );
      sync();
      new ResizeObserver(sync).observe(slide);
    }
  }

  customElements.define("swatch-carousel-latori", SwatchCarouselLatori);

  /**
   * Selecting a variant replaces the whole variant-picker markup (see
   * assets/variant-picker.js updateContent()). Left alone, that would
   * disconnect and recreate this element from scratch, tearing down the
   * Swiper instance and losing whatever page the shopper had paged to (and
   * visibly flashing while Swiper re-initializes).
   *
   * "before-replace" fires just before that swap, with both the outgoing
   * (still connected) and incoming (detached, freshly fetched) markup in
   * hand, so instead of letting the whole subtree be replaced, the already-
   * initialized carousel is patched in place with whatever changed (checked
   * swatch, availability, target variant id, sale badge) and then moved -
   * same node, same live Swiper instance - into the incoming markup ahead of
   * time, in place of its own freshly-fetched (and disposable) copy.
   */
  document.addEventListener("on:variant:before-replace-element", (evt) => {
    const { toReplace, replaceWith } = evt.detail;
    const oldCarousels = toReplace.querySelectorAll("swatch-carousel-latori");
    if (!oldCarousels.length) return;

    const newCarousels = replaceWith.querySelectorAll("swatch-carousel-latori");
    oldCarousels.forEach((oldCarousel, index) => {
      const newCarousel = newCarousels[index];
      if (!newCarousel) return;

      oldCarousel
        .querySelectorAll(".js-option[data-value-id]")
        .forEach((input) => {
          const newInput = newCarousel.querySelector(
            `.js-option[data-value-id="${input.dataset.valueId}"]`,
          );
          if (!newInput) return;
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          const oldLabel = input.nextElementSibling;
          const newLabel = newInput.nextElementSibling;
          if (oldLabel && newLabel) oldLabel.innerHTML = newLabel.innerHTML;
        });

      newCarousel.replaceWith(oldCarousel);

      // Right after this, vendor code restores focus to the clicked input
      // (assets/variant-picker.js, "Restore focus"). Swiper's a11y module
      // treats any focus it didn't itself mark as click-triggered as keyboard
      // navigation and "helpfully" scrolls that slide into view - using a raw
      // slide index rather than a page-aligned one, which Grid resolves to a
      // different (often first) page instead of the current one. Marking this
      // as click-triggered, the same way Swiper's own pointerdown handler
      // would have, suppresses that scroll; Swiper clears the flag itself
      // shortly after via its own two-rAF timer.
      if (oldCarousel.swiper) oldCarousel.swiper.a11y.clicked = true;
    });
  });
}
